import Button from '@/components/Button';
import ErrorMessage from '@/components/Form/ErrorMessage';
import FormGroup from '@/components/Form/FormGroup';
import LabelText from '@/components/Form/LabelText';
import Col from '@/components/Grid/Col';
import Row from '@/components/Grid/Row';
import AddressButton from '@/components/Input/AddressButton';
import InputRadio from '@/components/Input/InputRadio';
import InputText from '@/components/Input/InputText';
import InputTextarea from '@/components/Input/InputTextarea';
import { Tit } from '@/components/Titles';
import { breakpoint } from '@/helpers/BreakpointHelper';
import {
  birthRegex,
  emailRegex,
  mobileRegex,
} from '@/helpers/ValidationHelper';
import { FormButton, RightCol } from '@/page-blocks/auth/AuthCommon';
import dayjs from 'dayjs';
import React, { FC, useCallback } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import styled from 'styled-components';

const StepContainer = styled.div`
  header {
    line-height: 1.5;
    border-bottom: 1px solid #1cabe2;
    padding-bottom: 16px;
    margin-bottom: 24px;
  }
  ${FormGroup} {
    .tip {
      font-size: 14px;
      margin-top: 8px;
      font-weight: 100;
      ${breakpoint(640)} {
        font-size: 12px;
      }
    }
  }
  .agree-dl {
    margin-top: 24px;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;

    label {
      margin-left: 32px;

      &:first-child {
        margin-left: 0;
      }
    }
  }
  .btn-wrap {
    margin-top: 80px;
  }
  ${breakpoint(`mobile`)} {
    .agree-dl {
      margin-top: 16px;

      dt,
      dd {
        width: 100%;
      }
      dd {
        margin-top: 16px;
        label {
          margin: 0;
          width: 50%;
        }
      }
    }
  }
`;
export interface PartyReviewStep1Data {
  name: string;
  nameRep?: string;
  birthday: string;
  contact: string;
  email: string;
  zipcode: string;
  address: string;
  addressDetail: string;
  answerRoute: string;
}

interface PartyReviewCompleteProps {
  onSubmit: (data: PartyReviewStep1Data) => void;
  data?: any;
}
const ParrtyReviewStep1: FC<PartyReviewCompleteProps> = ({ onSubmit }) => {
  const {
    handleSubmit,
    register,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<PartyReviewStep1Data>({
    defaultValues: {
      name: ``,
      nameRep: ``,
      birthday: ``,
      contact: ``,
      email: ``,
      zipcode: ``,
      address: ``,
      addressDetail: ``,
      answerRoute: ``,
    },
  });
  const onSubmitForm: SubmitHandler<PartyReviewStep1Data> = useCallback(
    (formData) => {
      onSubmit(formData);
    },
    [onSubmit],
  );
  return (
    <StepContainer>
      <form onSubmit={handleSubmit(onSubmitForm)}>
        <div className="form-container">
          <header>
            <h3>
              <Tit size="s2">1. 인적사항</Tit>
            </h3>
          </header>
          <FormGroup className="FormGroup">
            <LabelText require>성명·단체명</LabelText>
            <InputText
              placeholder="성명·단체명 입력"
              name="name"
              ref={register({
                required: {
                  value: true,
                  message: `성명·단체명을 입력해주세요.`,
                },
                maxLength: {
                  value: 20,
                  message: `20자 이내로 입력해주세요.`,
                },
              })}
            />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            <p className="tip">
              단체명으로 신청 시 주최자명을 반드시 입력해 주세요.
            </p>
          </FormGroup>
          <FormGroup className="FormGroup">
            <LabelText>주최자명</LabelText>
            <InputText
              placeholder="주최자명 입력(선택)"
              name="nameRep"
              ref={register({
                maxLength: {
                  value: 20,
                  message: `20자 이내로 입력해주세요.`,
                },
              })}
            />
            {errors.nameRep && (
              <ErrorMessage>{errors.nameRep.message}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup className="FormGroup">
            <LabelText require>생년월일</LabelText>
            <InputText
              placeholder="생년월일 6자리 입력"
              type="number"
              name="birthday"
              ref={register({
                required: {
                  value: true,
                  message: `생년월일을 입력해주세요.`,
                },
                pattern: {
                  value: birthRegex,
                  message: `6자리 숫자로만 입력해주세요.`,
                },
                validate: (value) =>
                  dayjs(value, `YYMMDD`, true).isValid() ||
                  `올바르지 않은 형식입니다`,
              })}
            />
            {errors.birthday && (
              <ErrorMessage>{errors.birthday.message}</ErrorMessage>
            )}
            <p className="tip">만 7세 이상만 신청 가능합니다.</p>
          </FormGroup>
          <FormGroup className="FormGroup">
            <LabelText require>휴대폰번호</LabelText>
            <InputText
              placeholder="휴대폰번호 입력 ( &lsquo;-&rsquo; 제외 )"
              name="contact"
              ref={register({
                required: {
                  value: true,
                  message: `휴대폰번호를 입력해주세요.`,
                },
                pattern: {
                  value: mobileRegex,
                  message: `올바른 휴대폰번호가 아닙니다.`,
                },
                maxLength: {
                  value: 15,
                  message: `15자 이내로 입력해주세요.`,
                },
                validate: {
                  isValidRange: (value) =>
                    (value.substr(0, 3) === `010` && value.length === 11) ||
                    (value.substr(0, 3) !== `010` && value.length === 10) ||
                    `휴대폰번호 자릿수를 확인해주세요`,
                },
              })}
            />
            {errors.contact && (
              <ErrorMessage>{errors.contact.message}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup className="FormGroup">
            <LabelText require>이메일</LabelText>
            <InputText
              placeholder="이메일주소 입력"
              name="email"
              ref={register({
                required: {
                  value: true,
                  message: `이메일을 입력해주세요.`,
                },
                pattern: {
                  value: emailRegex,
                  message: `올바른 이메일 형식이 아닙니다.`,
                },
                maxLength: {
                  value: 70,
                  message: `70자 이내로 입력해주세요.`,
                },
              })}
            />
            {errors.email && (
              <ErrorMessage>{errors.email.message}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup className="FormGroup">
            <LabelText require>주소</LabelText>
            <Row>
              <Col desktop="auto">
                <InputText
                  name="zipcode"
                  readOnly
                  ref={register({
                    required: {
                      value: true,
                      message: `주소를 입력해주세요.`,
                    },
                  })}
                />
              </Col>
              <RightCol desktop="none">
                <AddressButton
                  onSelectAddress={(address) => {
                    setValue(`zipcode`, address.zonecode);
                    setValue(`address`, address.address);
                  }}
                  renderButton={(buttonProps) => (
                    <FormButton
                      {...buttonProps}
                      outline
                      css={`
                        ${breakpoint(768)} {
                          margin-top: 0 !important;
                        }
                      `}
                    >
                      주소검색
                    </FormButton>
                  )}
                />
              </RightCol>
            </Row>
            <Row>
              <InputText
                name="address"
                readOnly
                ref={register({
                  required: {
                    value: true,
                    message: `주소를 입력해주세요.`,
                  },
                })}
              />
            </Row>
            <Row>
              <InputText
                name="addressDetail"
                placeholder="상세주소 입력"
                ref={register}
              />
            </Row>
            {errors.zipcode && (
              <ErrorMessage>{errors.zipcode.message}</ErrorMessage>
            )}
          </FormGroup>
          <FormGroup className="FormGroup">
            <LabelText require>알게 된 경로</LabelText>
            <InputTextarea
              placeholder="유니세프 Party for UNICEF를 알게 된 경로를 입력해 주세요."
              name="answerRoute"
              ref={register({
                required: {
                  value: true,
                  message: `알게 된 경로를 입력해주세요.`,
                },
                maxLength: {
                  value: 500,
                  message: `500자 이내로 입력해주세요.`,
                },
              })}
            />
            {errors.answerRoute && (
              <ErrorMessage>{errors.answerRoute.message}</ErrorMessage>
            )}
          </FormGroup>
          <div className="dec agree">
            [필수] Party for UNICEF 모금활동에 대한 기부확인증 발급 및 후원관리
            목적으로 성명, 생년월일, 연락처, 주소, 이메일을 수집하며,
            후원일로부터 5년 간 보관합니다.
          </div>
          <dl className="agree-dl">
            <dt>개인정보 수집·이용에 동의하십니까?</dt>
            <dd>
              <InputRadio
                label="동의함"
                name="isAgreeYn"
                value="Y"
                ref={register({
                  required: {
                    value: true,
                    message: `개인정보 수집·이용에 동의해주세요.`,
                  },
                })}
              />
              <InputRadio label="동의하지 않음" name="isAgreeYn" value="N" />
            </dd>
          </dl>
          {errors.isAgreeYn && (
            <ErrorMessage>{errors.isAgreeYn.message}</ErrorMessage>
          )}
          <div className="btn-wrap">
            <Button type="submit" full>
              파티 후기 작성
            </Button>
          </div>
        </div>
      </form>
    </StepContainer>
  );
};
export default ParrtyReviewStep1;
